/* eslint-disable prettier/prettier */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDisclosure, SimpleGrid, Box, Spinner, Text, Flex } from '@chakra-ui/react';
import { PageBox } from '../../../../components/pageBox';
import { ICompany } from '../../../../hooks/useCompanies';
import { useTaxFilters } from '../../../../hooks/useTaxFilters';
import {
  useTaxAnalysis,
  ITaxFaturamentoSNResponse,
  ITaxFaturamentoSNItem,
} from '../../../../hooks/useTaxAnalysis';
import {
  calculateTaxPercentage,
  ITaxAnalysisFilters,
  ITaxAnalysisSNCompany,
} from '../../../../types/taxAnalysis';
import { TaxHeaderSN } from './components/TaxHeaderSN';
import { TaxSummaryCardsSN } from './components/TaxSummaryCardsSN';
import { TaxFiltersSN } from './components/TaxFiltersSN';
import { TaxTableSN } from './components/TaxTableSN';
import { TaxCardSN } from './components/TaxCardSN';
import { TaxDetailModalSN } from './components/TaxDetailModalSN';

export function ModulesHomeSupervisorTaxAnalysisSNPage() {
  const taxFilters = useTaxFilters({ screenData: 'simples_nacional' });
  const { getTaxAnalysisSNFaturamento, exportTaxAnalysisSNToCSV } = useTaxAnalysis();

  const {
    isOpen: isDetailModalOpen,
    onOpen: onDetailModalOpen,
    onClose: onDetailModalClose,
  } = useDisclosure();

  const [selectedCompanyData, setSelectedCompanyData] = useState<ITaxFaturamentoSNItem | null>(
    null,
  );
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [allCompaniesWithTax, setAllCompaniesWithTax] = useState<ITaxAnalysisSNCompany[]>([]);
  const [currentPage, setCurrentPage] = useState(() => {
    const pageFromFilters = Number(taxFilters.filters.get('page')) || 1;
    return pageFromFilters;
  });
  const [hasMore, setHasMore] = useState(true);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const previousFilterKeyRef = useRef<string>('');
  const isLoadingMoreRef = useRef(false);

  // Criar chave de filtros para detectar mudanças
  const filterKey = useMemo(() => {
    const filtersObj = Object.fromEntries([...Array.from(taxFilters.filters)]);
    // Remove page para comparar apenas filtros reais
    delete filtersObj.page;
    return JSON.stringify(filtersObj);
  }, [taxFilters.filters]);

  // Resetar quando filtros mudam (exceto página)
  useEffect(() => {
    if (previousFilterKeyRef.current && previousFilterKeyRef.current !== filterKey) {
      setAllCompaniesWithTax([]);
      setCurrentPage(1);
      setHasMore(true);
    }
    previousFilterKeyRef.current = filterKey;
  }, [filterKey]);

  // Criar filtros para a query incluindo currentPage
  const filtersForQuery = useMemo(() => {
    const baseFilters = Object.fromEntries([...Array.from(taxFilters.filters)]);
    // Garantir que page seja sempre atualizado com currentPage (como string para a API)
    return {
      ...baseFilters,
      page: String(currentPage),
      size: baseFilters.size || '20',
    };
  }, [taxFilters.filters, currentPage]);

  // Query para buscar empresas com faturamento e simples nacional
  const { data: dataCompanies, isFetching: isFetchingCompany } =
    useQuery<ITaxFaturamentoSNResponse>({
      queryKey: ['TaxFaturamentoSN', filtersForQuery],
      queryFn: getTaxAnalysisSNFaturamento,
    });

  const empresas = dataCompanies?.empresas ?? [];
  const total = dataCompanies?.total ?? 0;
  const totaisGerais = dataCompanies?.totais_gerais;

  // Transformar dados das empresas com valores da nova rota
  const companiesWithTax = useMemo(() => {
    return empresas
      .filter(item => item.TbEmpresa)
      .map(item => {
        const company = item.TbEmpresa;
        const totalFaturamento = item.total_faturamento || 0;
        const vlSimplesNacional = item.vl_simples_nacional || 0;

        return {
          id: company.id,
          external_id: company.external_id,
          razao_social: company.razao_social,
          cnpj: company.cnpj,
          total_faturamento: totalFaturamento,
          faturamento_acumulado: 0, // Não disponível na nova rota
          vl_simples_nacional: vlSimplesNacional,
          simples_nacional_percentual: calculateTaxPercentage(vlSimplesNacional, totalFaturamento),
          vl_saida: item.vl_saida || 0,
          vl_servico: item.vl_servico || 0,
          vl_outros: item.vl_outros || 0,
          iss: item.iss || 0,
          iss_retido: item.iss_retido || 0,
          anexo_simples_nacional: item.anexo_simples_nacional ?? undefined,
          secao_simples_nacional: item.secao_simples_nacional ?? undefined,
          tabela_simples_nacional: item.tabela_simples_nacional ?? undefined,
        } as ITaxAnalysisSNCompany;
      });
  }, [empresas]);

  // Criar mapa acumulado de todas as empresas de todas as páginas carregadas
  const [allCompaniesDataMap, setAllCompaniesDataMap] = useState<Map<number, ICompany>>(new Map());

  // Atualizar mapa acumulado quando empresas da página atual mudam
  useEffect(() => {
    if (!isFetchingCompany && empresas.length > 0) {
      setAllCompaniesDataMap(prev => {
        const newMap = new Map(prev);
        empresas.forEach(item => {
          if (item.TbEmpresa?.id) {
            newMap.set(item.TbEmpresa.id, item.TbEmpresa);
          }
        });
        return newMap;
      });
    }
  }, [empresas, isFetchingCompany]);

  // Resetar mapa quando filtros mudam
  useEffect(() => {
    if (previousFilterKeyRef.current && previousFilterKeyRef.current !== filterKey) {
      setAllCompaniesDataMap(new Map());
    }
  }, [filterKey]);

  // Criar mapa de companies acumuladas para lookup rápido
  const allCompaniesMap = useMemo(() => {
    return allCompaniesDataMap;
  }, [allCompaniesDataMap]);

  // Memoizar array de companies ordenadas para evitar sort a cada render
  const sortedCompaniesWithTax = useMemo(() => {
    return [...allCompaniesWithTax].sort((a, b) => b.total_faturamento - a.total_faturamento);
  }, [allCompaniesWithTax]);

  // Memoizar array de companies para modo table
  const tableCompanies = useMemo(() => {
    return sortedCompaniesWithTax
      .map(cwt => allCompaniesMap.get(cwt.id))
      .filter((c): c is ICompany => c !== undefined);
  }, [sortedCompaniesWithTax, allCompaniesMap]);

  // Criar mapa acumulado de companyData para lookup rápido (evita buscar apenas da página atual)
  const [allCompanyDataMap, setAllCompanyDataMap] = useState<Map<number, ITaxFaturamentoSNItem>>(
    new Map(),
  );

  // Atualizar mapa acumulado quando dados da página atual mudam
  useEffect(() => {
    if (!isFetchingCompany && empresas.length > 0) {
      setAllCompanyDataMap(prev => {
        const newMap = new Map(prev);
        empresas.forEach(item => {
          if (item.TbEmpresa?.id) {
            newMap.set(item.TbEmpresa.id, item);
          }
        });
        return newMap;
      });
    }
  }, [empresas, isFetchingCompany]);

  // Resetar mapa quando filtros mudam
  useEffect(() => {
    if (previousFilterKeyRef.current && previousFilterKeyRef.current !== filterKey) {
      setAllCompanyDataMap(new Map());
    }
  }, [filterKey]);

  // Acumular dados quando uma nova página é carregada, evitando duplicados
  useEffect(() => {
    // Não atualizar enquanto está carregando para evitar estados intermediários
    if (isFetchingCompany) {
      return;
    }

    // Resetar flag de loading quando dados chegam
    isLoadingMoreRef.current = false;

    if (companiesWithTax && companiesWithTax.length > 0) {
      if (currentPage === 1) {
        // Resetar quando é a primeira página (filtros mudaram)
        setAllCompaniesWithTax(companiesWithTax);
      } else {
        // Acumular quando é uma página subsequente, evitando duplicados
        setAllCompaniesWithTax(prev => {
          const existingIds = new Set(prev.map(item => item.external_id));
          const newItems = companiesWithTax.filter(item => !existingIds.has(item.external_id));
          return [...prev, ...newItems];
        });
      }
      // Verificar se há mais páginas
      const pageSize = Number(taxFilters.filters.get('size')) || 20;
      const totalPages = Math.ceil(total / pageSize);
      setHasMore(currentPage < totalPages);
    } else if (companiesWithTax && companiesWithTax.length === 0 && currentPage === 1) {
      // Resetar se não há dados na primeira página
      setAllCompaniesWithTax([]);
      setHasMore(false);
    }
  }, [companiesWithTax, currentPage, total, taxFilters.filters, isFetchingCompany]);

  // Callback para carregar mais páginas
  const loadMore = useCallback(() => {
    // O flag já foi verificado no observer, então podemos confiar que está ok
    if (hasMore && !isFetchingCompany) {
      setCurrentPage(prev => prev + 1);
    }
  }, [hasMore, isFetchingCompany]);

  // Intersection Observer para infinite scroll (funciona em ambos os modos)
  // Reduzido dependências para evitar recriação desnecessária
  useEffect(() => {
    if (!hasMore || isFetchingCompany) {
      return undefined;
    }

    let observer: IntersectionObserver | null = null;

    // Usar setTimeout para garantir que o DOM foi atualizado após renderização
    const timeoutId = setTimeout(() => {
      const element = loadMoreRef.current;
      if (!element) {
        return;
      }

      observer = new IntersectionObserver(
        entries => {
          // Verificar todas as condições antes de processar
          if (!entries[0].isIntersecting) {
            return;
          }

          // Verificar condições novamente dentro do callback
          if (!hasMore || isFetchingCompany || isLoadingMoreRef.current) {
            return;
          }

          // Desconectar observer ANTES de setar flag e chamar loadMore
          if (observer) {
            observer.disconnect();
            observer = null;
          }

          // Setar flag e chamar loadMore
          isLoadingMoreRef.current = true;
          loadMore();
        },
        { threshold: 0.1, rootMargin: '50px' },
      );

      observer.observe(element);
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [hasMore, isFetchingCompany, loadMore]);

  // Verificar se há filtros de busca específicos aplicados (com valor não vazio)
  const hasSearchFilters = useMemo(() => {
    const externalId = taxFilters.filters.get('external_id');
    const cnpj = taxFilters.filters.get('cnpj');
    const razaoSocial = taxFilters.filters.get('razao_social');
    return !!(externalId || cnpj || razaoSocial);
  }, [taxFilters.filters]);

  // Usar totais gerais da API ou calcular a partir das empresas filtradas
  const totals = useMemo(() => {
    // Se há filtros de busca, calcular totais a partir das empresas retornadas
    if (hasSearchFilters) {
      const totalFaturamento = empresas.reduce((acc, c) => acc + (c.total_faturamento || 0), 0);
      const totalSN = empresas.reduce((acc, c) => acc + (c.vl_simples_nacional || 0), 0);
      const mediaPercentual = totalFaturamento > 0 ? (totalSN / totalFaturamento) * 100 : 0;

      return {
        total_empresas: total,
        total_faturamento: totalFaturamento,
        total_simples_nacional: totalSN,
        media_percentual: mediaPercentual,
      };
    }

    // Sem filtros de busca, usar totais_gerais da API
    const totalFaturamento = totaisGerais?.total_faturamento ?? 0;
    const totalSN = totaisGerais?.vl_simples_nacional ?? 0;
    const mediaPercentual = totalFaturamento > 0 ? (totalSN / totalFaturamento) * 100 : 0;

    return {
      total_empresas: total,
      total_faturamento: totalFaturamento,
      total_simples_nacional: totalSN,
      media_percentual: mediaPercentual,
    };
  }, [totaisGerais, total, hasSearchFilters, empresas]);

  // Handler para abrir modal de detalhes - usa mapa acumulado
  const handleOpenDetail = useCallback(
    (company: ICompany) => {
      const companyData = allCompanyDataMap.get(company.id);
      setSelectedCompanyData(companyData || null);
      onDetailModalOpen();
    },
    [onDetailModalOpen, allCompanyDataMap],
  );

  return (
    <PageBox>
      <div className="flex flex-col gap-2">
        <TaxHeaderSN
          title="% SIMPLES NACIONAL / FATURAMENTO"
          subtitle="Analise a porcentagem que o Simples Nacional representa sobre o faturamento"
          iconBgColor="#FFF8E1"
          iconColor="#FFA000"
          filters={Object.fromEntries(taxFilters.filters) as ITaxAnalysisFilters}
          onExportCSV={exportTaxAnalysisSNToCSV}
        />

        <TaxSummaryCardsSN
          totalEmpresas={totals.total_empresas}
          totalFaturamento={totals.total_faturamento}
          totalSimplesNacional={totals.total_simples_nacional}
          mediaPercentual={totals.media_percentual}
          isLoading={isFetchingCompany}
        />

        <TaxFiltersSN
          {...taxFilters}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onSearchChange={taxFilters.handleSearchChange}
        />

        {viewMode === 'table' && (
          <>
            <TaxTableSN
              companies={tableCompanies}
              companiesWithTax={allCompaniesWithTax}
              isLoading={isFetchingCompany && allCompaniesWithTax.length === 0}
              orderBy={taxFilters.orderBy}
              orderDirection={taxFilters.orderDirection}
              onRowClick={handleOpenDetail}
              onSort={taxFilters.handleSort}
            />
            {hasMore && (
              <Box ref={loadMoreRef} py={4} display="flex" justifyContent="center" minH="50px">
                {isFetchingCompany && <Spinner size="lg" color="blue.500" />}
              </Box>
            )}
          </>
        )}

        {viewMode === 'grid' && (
          <>
            {isFetchingCompany && allCompaniesWithTax.length === 0 && (
              <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
                <Flex justify="center" align="center" h="400px">
                  <Spinner size="xl" color="blue.500" />
                </Flex>
              </Box>
            )}

            {!isFetchingCompany && allCompaniesWithTax.length === 0 && (
              <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
                <Flex justify="center" align="center" h="400px">
                  <Text color="gray.500">Nenhuma empresa encontrada</Text>
                </Flex>
              </Box>
            )}

            {allCompaniesWithTax.length > 0 && (
              <>
                <SimpleGrid columns={{ base: 1, sm: 2, xl: 3 }} spacing={{ base: 2, md: 4 }}>
                  {sortedCompaniesWithTax.map(companyWithTax => {
                    const company = allCompaniesMap.get(companyWithTax.id);
                    if (!company) return null;
                    return (
                      <TaxCardSN
                        key={companyWithTax.external_id}
                        company={company}
                        companyWithTax={companyWithTax}
                        onClick={handleOpenDetail}
                      />
                    );
                  })}
                </SimpleGrid>
                {hasMore && (
                  <Box ref={loadMoreRef} py={4} display="flex" justifyContent="center" minH="50px">
                    {isFetchingCompany && <Spinner size="lg" color="blue.500" />}
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </div>

      <TaxDetailModalSN
        isOpen={isDetailModalOpen}
        onClose={onDetailModalClose}
        companyData={selectedCompanyData}
        dateStart={taxFilters.filters.get('date_start') || undefined}
        dateEnd={taxFilters.filters.get('date_end') || undefined}
      />
    </PageBox>
  );
}
